/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Three Card PD component — 3-up editorial image grid with centered link labels
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';

// RGBARRY-CUSTOMIZATION: validate the merchant-authored section color before applying inline CSS.
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const parseOptionalHex = (value: string | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : undefined;
};

/* v8 ignore start */
@Component('threeCard', {
    name: '3 Card',
    description: '3-up editorial image grid. Each card has an image and a centered link label below.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ThreeCardMetadata {
    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color Hex',
        description: 'Optional #RGB or #RRGGBB color. Leave empty to use the storefront background color.',
        type: 'string',
    })
    backgroundColor?: string;

    @AttributeDefinition({ id: 'card1Image', name: 'Card 1 Image', type: 'image' })
    card1Image?: Image;

    @AttributeDefinition({ id: 'card1ImageAlt', name: 'Card 1 Image Alt Text', type: 'string' })
    card1ImageAlt?: string;

    @AttributeDefinition({ id: 'card1Label', name: 'Card 1 Label', type: 'string' })
    card1Label?: string;

    @AttributeDefinition({ id: 'card1Link', name: 'Card 1 Link', type: 'url' })
    card1Link?: string;

    @AttributeDefinition({ id: 'card2Image', name: 'Card 2 Image', type: 'image' })
    card2Image?: Image;

    @AttributeDefinition({ id: 'card2ImageAlt', name: 'Card 2 Image Alt Text', type: 'string' })
    card2ImageAlt?: string;

    @AttributeDefinition({ id: 'card2Label', name: 'Card 2 Label', type: 'string' })
    card2Label?: string;

    @AttributeDefinition({ id: 'card2Link', name: 'Card 2 Link', type: 'url' })
    card2Link?: string;

    @AttributeDefinition({ id: 'card3Image', name: 'Card 3 Image', type: 'image' })
    card3Image?: Image;

    @AttributeDefinition({ id: 'card3ImageAlt', name: 'Card 3 Image Alt Text', type: 'string' })
    card3ImageAlt?: string;

    @AttributeDefinition({ id: 'card3Label', name: 'Card 3 Label', type: 'string' })
    card3Label?: string;

    @AttributeDefinition({ id: 'card3Link', name: 'Card 3 Link', type: 'url' })
    card3Link?: string;
}
/* v8 ignore stop */

interface CardProps {
    image?: Image;
    imageAlt?: string;
    label?: string;
    link?: string;
    inView: boolean;
    delay: number;
}

function ThreeCardItem({ image, imageAlt, label, link, inView, delay }: CardProps) {
    // RGBARRY-CUSTOMIZATION: animate the wrapper so DynamicImage's image fade cannot override the zoom transition.
    const inner = (
        <div
            className={cn('group flex flex-col gap-4 df-reveal', inView && 'df-reveal--show')}
            style={{ transitionDelay: inView ? `${delay}ms` : '0ms' }}>
            <div className="relative overflow-hidden aspect-square bg-muted rounded-ui">
                {image?.url && (
                    <DynamicImage
                        src={image.url}
                        alt={imageAlt ?? ''}
                        widths={{ base: 600, lg: 560 }}
                        className="absolute inset-0 w-full h-full transition-transform duration-700 ease-in-out motion-reduce:transition-none group-hover:scale-105 motion-reduce:group-hover:scale-100"
                        imageProps={{
                            className: 'w-full h-full object-cover',
                        }}
                    />
                )}
            </div>
            {label && <p className="text-sm text-center text-foreground underline underline-offset-4">{label}</p>}
        </div>
    );
    // END RGBARRY-CUSTOMIZATION

    if (link) {
        return <Link to={link}>{inner}</Link>;
    }
    return inner;
}

export default function ThreeCard({
    backgroundColor,
    card1Image,
    card1ImageAlt,
    card1Label,
    card1Link,
    card2Image,
    card2ImageAlt,
    card2Label,
    card2Link,
    card3Image,
    card3ImageAlt,
    card3Label,
    card3Link,
}: {
    backgroundColor?: string;
    card1Image?: Image;
    card1ImageAlt?: string;
    card1Label?: string;
    card1Link?: string;
    card2Image?: Image;
    card2ImageAlt?: string;
    card2Label?: string;
    card2Link?: string;
    card3Image?: Image;
    card3ImageAlt?: string;
    card3Label?: string;
    card3Link?: string;
}) {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-80px' });
    const authoredBackgroundColor = parseOptionalHex(backgroundColor);

    const cards: CardProps[] = [
        { image: card1Image, imageAlt: card1ImageAlt, label: card1Label, link: card1Link, inView, delay: 0 },
        { image: card2Image, imageAlt: card2ImageAlt, label: card2Label, link: card2Link, inView, delay: 80 },
        { image: card3Image, imageAlt: card3ImageAlt, label: card3Label, link: card3Link, inView, delay: 160 },
    ];

    return (
        <section
            ref={ref}
            className="bg-background py-10 lg:py-14"
            style={authoredBackgroundColor ? { backgroundColor: authoredBackgroundColor } : undefined}>
            {/* Desktop: 3-column equal grid (unchanged) */}
            <div className="hidden lg:block section-container px-6 lg:px-8">
                <div className="grid grid-cols-3 gap-6">
                    {/* oxlint-disable react/no-array-index-key */}
                    {cards.map((card, i) => (
                        <ThreeCardItem key={i} {...card} />
                    ))}
                    {/* oxlint-enable react/no-array-index-key */}
                </div>
            </div>

            {/* Mobile/tablet: full-width swipeable carousel — larger readable cards with a peek of the next */}
            <div className="lg:hidden">
                <Carousel opts={{ align: 'start', dragFree: true }} className="w-full">
                    <CarouselContent className="pl-6">
                        {/* oxlint-disable react/no-array-index-key */}
                        {cards.map((card, i) => (
                            <CarouselItem key={i} className="basis-[62%] min-w-0 pr-4">
                                <ThreeCardItem {...card} />
                            </CarouselItem>
                        ))}
                        {/* oxlint-enable react/no-array-index-key */}
                    </CarouselContent>
                </Carousel>
            </div>
        </section>
    );
}
